import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/auth";
import { updateUserLegacy } from "@/features/userProfile/services/userProfile.service";
import { useErrorHandler } from "@/shared/hooks/useErrorHandler";
import type { UpdateUserData, User, UserResponse } from "@/shared/types/";
import type { CustomAttributesResponse } from "@/shared/types/askedAttributes.types";

export type UpdateUserMutation = ReturnType<typeof useMutation<UserResponse, unknown, UpdateUserData>>;

type UseUserAddOns = {
  hasUser: boolean
  initials: string
  fullName: string
  abbreviatedName: string
  updateUserState: (updatedUserData: Partial<UserResponse["employee"]>) => void
  updateUserMutation: UpdateUserMutation
};

type UseUserReturn = Partial<User> & UseUserAddOns;

export const useUser = (): UseUserReturn => {
  const { user, updateUserState } = useAuth();
  const { handleError } = useErrorHandler();
  const queryClient = useQueryClient();
  const userData = user?.employee;
  const { first_name, last_name } = userData ?? {};

  const toUser = (updateUserData: UpdateUserData): User | undefined => {
    if (!userData) return;

    const {
      avatar, zip_code, custom_attributes, gender, ...rest
    } = updateUserData;
    const normalizedGender: User["gender"] | undefined = gender === "" ? null : gender;

    const mergedCustomAttributes = [...(userData.custom_attributes || [])];
    if (custom_attributes && Array.isArray(custom_attributes)) {
      custom_attributes.forEach((newAttr) => {
        const existingIndex = mergedCustomAttributes.findIndex(
          (existing) => existing.attribute_id === newAttr.attribute_id,
        );

        if (existingIndex >= 0) {
          mergedCustomAttributes[existingIndex] = {
            ...mergedCustomAttributes[existingIndex],
            ...(newAttr.value_id !== undefined && { value_id: newAttr.value_id }),
            ...(newAttr.value_name !== undefined && { value_name: newAttr.value_name }),
            ...(newAttr.free_value !== undefined && { free_value: newAttr.free_value }),
          };
        }
        else {
          mergedCustomAttributes.push({
            attribute_id: newAttr.attribute_id,
            attribute_name:
              newAttr.attribute_name || `Attribute ${newAttr.attribute_id}`,
            attribute_type: newAttr.attribute_type || "select",
            value_type: newAttr.value_type || "predefined",
            value_id: newAttr.value_id,
            value_name: newAttr.value_name,
            free_value: newAttr.free_value,
          });
        }
      });
    }

    return {
      ...userData,
      ...rest,
      ...(normalizedGender !== undefined ? { gender: normalizedGender } : {}),
      ...(zip_code !== undefined ? { zip_code: String(zip_code) } : {}),
      custom_attributes: mergedCustomAttributes,
    };
  };

  const [savedPreviousUser, setSavedPreviousUser] = useState<User | null>(null);

  const optimisticMutate = (input: UpdateUserData) => {
    const updatedUser = toUser(input);
    setSavedPreviousUser(userData ?? null);
    updateUserState(updatedUser ?? {});
  };

  const revertMutation = () => {
    updateUserState(savedPreviousUser ?? {});
    setSavedPreviousUser(null);
  };

  const updateUserMutation = useMutation({
    mutationFn: updateUserLegacy,
    meta: { invalidateResources: ["user"] },
    onMutate: optimisticMutate,
    onSuccess: (response) => {
      setSavedPreviousUser(null);
      updateUserState(response.employee);
      queryClient.setQueryData<CustomAttributesResponse>(
        ["userMandatoryFields"],
        (old) => (old ? { ...old, missing_mandatory_fields: [] } : old),
      );
    },
    onError: (error: unknown) => {
      revertMutation();
      handleError(error, { title: "Erreur de mise à jour du profil" });
    },
  });

  const formatInitials = (): string => {
    if (!first_name || !last_name) return "";
    return `${first_name.split("")[0]}${last_name.split("")[0]}`
      .toUpperCase()
      .trim();
  };

  const formatFullName = (): string => {
    if (!first_name || !last_name) return "";
    return `${first_name} ${last_name}`.trim();
  };

  const formatAbbreviatedName = (): string => {
    if (!first_name || !last_name) return "";
    return `${first_name?.split("")[0]} ${last_name}`.toUpperCase().trim();
  };

  return {
    ...userData,
    hasUser: !!userData,
    initials: formatInitials(),
    fullName: formatFullName(),
    abbreviatedName: formatAbbreviatedName(),
    updateUserState,
    updateUserMutation,
  };
};
